import { useState } from 'react';
import { supportBoardAPI } from '../../api/client';
import { COLORS, RADIUS, btn, input as inputStyle } from '../../utils/uiStyles';
import AssigneeTags, { assigneeIdsOf } from './AssigneeTags';

function apiErrorMessage(error) {
  return error.response?.data?.detail || error.message || '저장하지 못했습니다.';
}

// 등록된 일정의 제목·기간·담당자를 고치는 팝업. 구조는 ProjectModal 과 같게 맞췄다.
// 🔴 done·kind 는 보내지 않는다 — 완료 여부·목표 종류를 건드리면 진행률/상태 자동전환에 개입한다.
export default function ScheduleModal({ schedule, assignees, onClose, onSaved }) {
  const [title, setTitle] = useState(schedule?.title || '');
  const [startDate, setStartDate] = useState(schedule?.start_date || '');
  const [endDate, setEndDate] = useState(schedule?.end_date || '');
  const [assigneeIds, setAssigneeIds] = useState(() => assigneeIdsOf(schedule));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('일정 제목을 입력하세요.');
      return;
    }
    if (!startDate || !endDate) {
      setError('일정 제목과 기간을 입력하세요.');
      return;
    }
    if (startDate > endDate) {
      setError('종료일은 시작일보다 빠를 수 없습니다.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await supportBoardAPI.updateSchedule(schedule.id, {
        title: trimmedTitle,
        start_date: startDate,
        end_date: endDate,
        assignee_user_ids: assigneeIds,
        // 조각1(서버 복수 담당자)이 아직 안 올라간 환경에서도 대표 1명은 저장된다.
        assignee_user_id: assigneeIds[0] ?? null,
      });
      onSaved(response.data);
    } catch (saveError) {
      setError(apiErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'rgba(17,24,39,.45)',
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-schedule-modal-title"
        onSubmit={submit}
        style={{
          width: 'min(460px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: '24px',
          borderRadius: RADIUS.lg,
          background: COLORS.surface,
          boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 id="support-schedule-modal-title" style={{ margin: '0 0 20px', color: COLORS.text, fontSize: '19px' }}>
          일정 수정
        </h2>

        <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
          제목
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={200}
            required
            style={{ ...inputStyle, marginTop: '6px' }}
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
            시작일
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              style={{ ...inputStyle, marginTop: '6px' }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
            종료일
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              style={{ ...inputStyle, marginTop: '6px' }}
            />
          </label>
        </div>

        <div style={{ marginBottom: '14px' }}>
          <div style={{ marginBottom: '6px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>담당자</div>
          <AssigneeTags
            ids={assigneeIds}
            people={assignees}
            onChange={setAssigneeIds}
            groupLabel="일정 담당자"
          />
        </div>

        {error && (
          <div role="alert" style={{ marginBottom: '14px', color: COLORS.dangerText, fontSize: '13px' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={btn('secondary')}>취소</button>
          <button type="submit" disabled={saving} style={{ ...btn('primary'), opacity: saving ? 0.65 : 1 }}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
    </div>
  );
}
